import { ExamEventExamineeStatus, ExamResult, type TagCategory } from '@exam-center/graphql';
import { EllipsisText, Text } from '@shared/components';
import type { TableBodyRow } from '@shared/table-generator';

import {
  type OrderedQConfigStructure,
  type ExamEventScoreReport,
} from '../../../providers/exam-event-score-report-provider/exam-event-score-report-provider';

export interface ScoreReportTableRow {
  id: string;
  no: string;
  fullName: string;
  idNumber: string;
  course: string;
  totalScore: string;
  result: string;
  tagScores: string[];
}

export function mapResultLabel(status: ExamEventExamineeStatus, result: ExamResult | null) {
  if (status === ExamEventExamineeStatus.Absent) return 'ขาดสอบ';
  if (status === ExamEventExamineeStatus.Canceled) return 'ยกเลิก';
  if (result === ExamResult.Passed) return 'ผ่าน';
  if (result === ExamResult.Failed) return 'ไม่ผ่าน';

  return '-';
}

export function formatScoreByStatus(
  status: ExamEventExamineeStatus,
  score: number | null | undefined,
) {
  if (status === ExamEventExamineeStatus.Absent || status === ExamEventExamineeStatus.Canceled) {
    return '—';
  }

  if (score === null || score === undefined) {
    return '—';
  }

  return `${score}`;
}

export function createScoreReportTableRows(params: {
  scoreReports: ExamEventScoreReport[];
  orderedQConfigStructures: OrderedQConfigStructure[];
  selectedCourseLabel: string;
  selectedTagCategory: TagCategory | null;
}) {
  const { scoreReports, orderedQConfigStructures, selectedCourseLabel, selectedTagCategory } =
    params;

  const orderedQConfigStructureIds = orderedQConfigStructures.map(
    (orderedQConfigStructure) => orderedQConfigStructure.qConfigStructure.id,
  );

  return scoreReports.map<ScoreReportTableRow>((scoreReport, index) => {
    const fullName = [
      scoreReport.title,
      scoreReport.firstName,
      scoreReport.middleName,
      scoreReport.lastName,
    ]
      .filter(Boolean)
      .join(' ');

    const scoreByQConfigStructureId = new Map(
      scoreReport.resultByTags
        .filter((tag) => tag.category === selectedTagCategory)
        .map((tag) => [tag.refId, formatScoreByStatus(scoreReport.status, tag.score)]),
    );

    const tagScores = orderedQConfigStructureIds.map(
      (qConfigStructureId) => scoreByQConfigStructureId.get(qConfigStructureId) ?? '—',
    );

    return {
      id: `${scoreReport.idNumber}-${index}`,
      no: `${scoreReport.seatNumber ?? '—'}`,
      fullName,
      idNumber: scoreReport.idNumber,
      course: selectedCourseLabel,
      totalScore: formatScoreByStatus(scoreReport.status, scoreReport.totalScore),
      result: mapResultLabel(scoreReport.status, scoreReport.result),
      tagScores,
    };
  });
}

export function createTableBodyRows(params: {
  rows: ScoreReportTableRow[];
  visibleTagCount: number;
}) {
  const { rows, visibleTagCount } = params;

  return rows.map<TableBodyRow>((row) => {
    const scoreCells =
      visibleTagCount > 0
        ? Array.from({ length: visibleTagCount }, (_, index) => ({
            children: row.tagScores[index] ?? '—',
            align: 'center' as const,
            sx: {
              width: '80px',
            },
          }))
        : [];

    return {
      id: row.id,
      children: [
        {
          children: row.no,
          align: 'center',
        },
        {
          children: row.fullName,
        },
        {
          children: row.idNumber,
        },
        {
          children: <EllipsisText>{row.course}</EllipsisText>,
          align: 'center',
        },
        ...scoreCells,
        {
          children: <Text variant='body1Semibold'>{row.totalScore}</Text>,
          align: 'center',
        },
        {
          children: <Text variant='body1Semibold'>{row.result}</Text>,
          align: 'center',
        },
      ],
    };
  });
}
